import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { ContentLayoutProps, ContentLayout } from '@lumapps/content-layout/components/ContentLayout';
import { Page, PAGE_WIDTH, PageProps } from '@lumapps/lumx-front-office/components/Page';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { getContentPageId } from '../../utils/getContentPageId';

import './index.scss';

const CLASSNAME = 'content-page';

interface BaseContentPageProps {
    contentId: string;
    contentSlug: string | undefined;
    contentTitle: string | null | undefined;
    contentType: string | undefined;
    isHomePage: boolean;
    before?: React.ReactNode;
    after?: React.ReactNode;
    /** List of authorized widgets. */
    enabledWidgetTypes?: ContentLayoutProps['enabledWidgetTypes'];
    /** Page content ref. */
    contentRef?: ContentLayoutProps['contentRef'];
    onStatusChange?: ContentLayoutProps['onStatusChange'];
}

/**
 * Base content page component shared between front-office and webview
 */
export const BaseContentPage: React.FC<BaseContentPageProps> = (props) => {
    const {
        contentId,
        contentSlug,
        contentTitle,
        contentType,
        isHomePage,
        after,
        before,
        enabledWidgetTypes,
        contentRef,
        onStatusChange,
    } = props;
    const { block } = useClassnames(CLASSNAME);
    const [status, setStatus] = React.useState<BaseLoadingStatus>(BaseLoadingStatus.loading);

    const handleStatusChange: ContentLayoutProps['onStatusChange'] = (status) => {
        setStatus(status);
        onStatusChange?.(status);
    };

    if (!contentId) {
        return null;
    }

    return (
        <Page
            // Content is a specific page, so we use the content ID as the page ID.
            id={getContentPageId(contentId, contentSlug) as PageProps['id']}
            className={block({ 'is-homepage': isHomePage, [`type-${contentType}`]: true })}
            hasBackground={false}
            title={contentTitle || ''}
            isLoading={status === BaseLoadingStatus.loading}
            width={PAGE_WIDTH.FULL}
            type={contentType}
            entityId={contentType}
        >
            {before}
            <ContentLayout
                contentRef={contentRef}
                contentId={contentId}
                enabledWidgetTypes={enabledWidgetTypes}
                onStatusChange={handleStatusChange}
            />
            {after}
        </Page>
    );
};
